import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation } from 'react-router-dom';
import { getStoreUrl } from '../utils/storeUrls';
import MinutePacks from './MinutePacks';

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="8" fill="currentColor" fillOpacity="0.12" />
    <path d="M5 8l2.5 2.5L11 5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PricingSection = () => {
  const { t, i18n } = useTranslation();
  const [isAnnual, setIsAnnual] = useState(false);
  const { locale } = useParams<{ locale?: string }>();
  const location = useLocation();
  const getLocaleFromPath = () => {
    const segments = location.pathname.split('/').filter(Boolean);
    const supported = ['en', 'es', 'fr', 'de', 'pt', 'vi', 'sv', 'ro'];
    if (segments.length > 0 && supported.includes(segments[0])) return segments[0];
    return i18n.language || 'en';
  };
  const currentLocale = locale || getLocaleFromPath();

  const plans = [
    {
      key: 'free',
      name: t('pricing.plans.free.name', 'Free'),
      price: t('pricing.plans.free.price', '$0'),
      period: t('pricing.plans.free.period', 'forever'),
      features: Array.isArray(t('pricing.plans.free.features', { returnObjects: true }))
        ? (t('pricing.plans.free.features', { returnObjects: true }) as string[])
        : ['10 min of recording a day', '30 languages', 'Bullet-style summaries', 'Markdown export'],
      cta: t('pricing.plans.free.cta', 'Get Started'),
      ctaHref: getStoreUrl(i18n.language),
      highlighted: false,
    },
    {
      key: 'premium',
      name: t('pricing.plans.standard.name', 'Premium'),
      price: isAnnual
        ? t('pricing.plans.standard.priceAnnual', '$4.17')
        : t('pricing.plans.standard.price', '$7.99'),
      period: t('pricing.plans.standard.period', 'per month'),
      billedNote: isAnnual
        ? t('pricing.plans.standard.priceAnnualNote', 'billed as $49.99/year')
        : null,
      badge: t('pricing.plans.standard.badge', 'Most popular'),
      features: Array.isArray(t('pricing.plans.standard.features', { returnObjects: true }))
        ? (t('pricing.plans.standard.features', { returnObjects: true }) as string[])
        : [
            '1,800 min/month',
            'All 30 languages + live translate',
            'Smart summaries — flashcards, quizzes, meetings, todos & more',
            'Premium AI models (AssemblyAI, GPT-4o) with speaker diarization',
            'Privacy mode — fully device-only processing',
          ],
      cta: t('pricing.plans.standard.cta', 'Start 14-day trial'),
      ctaHref: getStoreUrl(i18n.language),
      highlighted: true,
    },
    {
      key: 'enterprise',
      name: t('pricing.plans.enterprise.name', 'Enterprise'),
      price: t('pricing.plans.enterprise.price', 'Custom'),
      period: t('pricing.plans.enterprise.period', 'per organization'),
      features: Array.isArray(t('pricing.plans.enterprise.features', { returnObjects: true }))
        ? (t('pricing.plans.enterprise.features', { returnObjects: true }) as string[])
        : [
            'Custom AI models',
            'Shared vocab + templates',
            'DPA, BAA, on-prem option',
            'Priority support',
          ],
      cta: t('pricing.plans.enterprise.cta', 'Contact Sales'),
      ctaHref: `/${currentLocale}/contact`,
      highlighted: false,
    },
  ];

  return (
    <section
      id="pricing"
      style={{ background: 'var(--v2-bg-subtle)', color: 'var(--v2-ink)', position: 'relative', zIndex: 2 }}
      className="py-24 px-4"
    >
      <div className="max-w-6xl mx-auto">
        {/* Heading */}
        <div className="text-center mb-12">
          <h2
            style={{
              fontSize: 'clamp(28px, 3.5vw, 40px)',
              fontWeight: 800,
              color: 'var(--v2-ink)',
              marginBottom: '12px',
            }}
          >
            {t('pricing.title', 'Choose Your Plan')}
          </h2>
          <p style={{ fontSize: '17px', color: 'var(--v2-ink-muted)', marginBottom: '28px' }}>
            {t('pricing.subtitle', 'Start free and upgrade as you grow')}
          </p>

          {/* Monthly / Annual toggle */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0',
              background: 'var(--v2-card)',
              border: '1px solid var(--v2-line)',
              borderRadius: '999px',
              padding: '4px',
            }}
          >
            <button
              onClick={() => setIsAnnual(false)}
              style={{
                padding: '8px 20px',
                borderRadius: '999px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
                background: !isAnnual ? 'var(--v2-primary)' : 'transparent',
                color: !isAnnual ? '#fff' : 'var(--v2-ink-muted)',
                transition: 'all 0.2s',
              }}
            >
              {t('pricing.toggle.monthly', 'Monthly')}
            </button>
            <button
              onClick={() => setIsAnnual(true)}
              style={{
                padding: '8px 20px',
                borderRadius: '999px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
                background: isAnnual ? 'var(--v2-primary)' : 'transparent',
                color: isAnnual ? '#fff' : 'var(--v2-ink-muted)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s',
              }}
            >
                {t('pricing.toggle.annual', 'Annually')} · {t('pricing.toggle.save', 'Save 48%')}
            </button>
          </div>
        </div>

        {/* Cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '24px',
            alignItems: 'start',
          }}
        >
          {plans.map((plan) => (
            <div
              key={plan.key}
              style={{
                background: plan.highlighted ? 'var(--v2-primary)' : 'var(--v2-card)',
                border: plan.highlighted
                  ? '1px solid var(--v2-primary)'
                  : '1px solid var(--v2-line)',
                borderRadius: '20px',
                padding: '32px 28px',
                position: 'relative',
                transform: plan.highlighted ? 'translateY(-4px)' : 'none',
                boxShadow: plan.highlighted
                  ? '0 30px 60px -20px hsl(248 76% 59% / .5)'
                  : '0 2px 12px rgba(0,0,0,0.04)',
                color: plan.highlighted ? '#fff' : 'var(--v2-ink)',
              }}
            >
              {plan.badge && (
                <div
                  style={{
                    position: 'absolute',
                    top: '-12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: plan.highlighted ? '#fff' : 'var(--v2-card)',
                    color: 'var(--v2-primary)',
                    border: plan.highlighted ? 'none' : '1px solid var(--v2-line)',
                    fontSize: '11px',
                    fontWeight: 700,
                    padding: '4px 14px',
                    borderRadius: '999px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {plan.badge}
                </div>
              )}

              <div
                style={{
                  fontSize: '13px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.07em',
                  color: plan.highlighted ? 'rgba(255,255,255,0.7)' : 'var(--v2-ink-muted)',
                  marginBottom: '12px',
                }}
              >
                {plan.name}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <span
                  style={{
                    fontSize: 'clamp(36px, 4vw, 48px)',
                    fontWeight: 800,
                    lineHeight: 1,
                    color: plan.highlighted ? '#fff' : 'var(--v2-ink)',
                  }}
                >
                  {plan.price}
                </span>
                <span
                  style={{
                    fontSize: '14px',
                    color: plan.highlighted ? 'rgba(255,255,255,0.65)' : 'var(--v2-ink-muted)',
                    marginLeft: '6px',
                  }}
                >
                  {plan.period}
                </span>
                {'billedNote' in plan && plan.billedNote && (
                  <div style={{ fontSize: '12px', color: plan.highlighted ? 'rgba(255,255,255,0.6)' : 'var(--v2-ink-muted)', marginTop: '4px' }}>
                    {plan.billedNote}
                  </div>
                )}
              </div>

              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 28px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {plan.features.map((feature, i) => (
                  <li
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px',
                      fontSize: '14px',
                      color: plan.highlighted ? 'rgba(255,255,255,0.85)' : 'var(--v2-ink-muted)',
                    }}
                  >
                    <span
                      style={{
                        color: plan.highlighted ? '#fff' : 'var(--v2-primary)',
                        flexShrink: 0,
                        marginTop: '1px',
                      }}
                    >
                      <CheckIcon />
                    </span>
                    {feature}
                  </li>
                ))}
              </ul>

              <a
                href={plan.ctaHref}
                target={plan.key !== 'enterprise' ? '_blank' : undefined}
                rel={plan.key !== 'enterprise' ? 'noopener noreferrer' : undefined}
                style={{
                  display: 'block',
                  textAlign: 'center',
                  padding: '13px',
                  borderRadius: '999px',
                  fontWeight: 700,
                  fontSize: '15px',
                  textDecoration: 'none',
                  background: plan.highlighted ? '#fff' : 'var(--v2-primary)',
                  color: plan.highlighted ? 'var(--v2-primary)' : '#fff',
                  transition: 'opacity 0.15s',
                }}
              >
                {plan.cta}
              </a>
            </div>
          ))}
        </div>

        <MinutePacks />
      </div>
    </section>
  );
};

export default PricingSection;
